/**
 * สร้างไฟล์ report "เวลาเข้า-ออกรายเดือน" ของทั้งบริษัท — ตรรกะล้วน ไม่พึ่ง React/DOM
 * (ทดสอบได้). ไฟล์เดียวมี 2 ส่วน:
 *   ส่วนบน  สรุปรายคน — นับจากรายวันในไฟล์นี้ เทียบกับตัวเลขที่ระบบใช้คิดเงินจริง
 *                       (จาก "สรุปเวลาเข้างาน") ให้ HR กระทบยอดกับที่จดมือได้ทันที
 *   ส่วนล่าง รายละเอียดรายวัน — 1 แถวต่อ 1 คนต่อ 1 วัน (เข้ากี่โมง ออกกี่โมง สายกี่นาที)
 */
import type { AttendanceExport } from '../hooks';

/** ครอบค่าด้วย " เมื่อมี , หรือขึ้นบรรทัดใหม่ (กติกา CSV) */
const esc = (v: string | number) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const round = (n: number) => Math.round(n * 1000) / 1000;

/** ป้ายชนิด OT — ต้องตรงกับ OT_TYPE_TH ฝั่ง worker (handlers/approval.ts) */
const OT_TYPE_LABEL: Record<string, string> = {
  ot_1: 'OT วันทำงาน',
  ot_2: 'OT วันหยุด',
  ot_3: 'OT เกินเวลาวันหยุด',
};

/**
 * 🚨 บรรทัดนี้ต้องอยู่ในไฟล์เสมอ — คอลัมน์ "OT อนุมัติ" กับ "OT ที่คิดเงิน" ไม่เท่ากัน
 * โดยตั้งใจ ไม่ใช่ระบบคำนวณพลาด ถ้าไม่เขียนไว้ HR จะเห็นสองเลขต่างกันแล้วแจ้งว่าเป็นบั๊ก
 */
/**
 * 🚨 ข้อความนี้ต้องอยู่ในไฟล์เสมอ และต้อง "ตรงกับที่ระบบทำจริง"
 *
 * ระบบคิดเงิน (reconcile) บวกชั่วโมงจากใบ OT ที่อนุมัติตรง ๆ สองคอลัมน์จึงปกติเท่ากัน
 * ⛔ ห้ามเขียนว่า "ถูกตัดก่อนจ่าย" — reconcile ไม่ได้ตัดด้วยเวลากะ/เวลาสแกนออก/
 *    เพดาน 36 ชม. (ฟังก์ชันพวกนั้นมีอยู่ใน ot-calc.ts แต่ยังไม่ถูกเรียก)
 *    เคยเขียนผิดแบบนั้นมาแล้วรอบหนึ่ง = อธิบายพฤติกรรมที่ไม่มีอยู่จริง
 *
 * ที่ทำให้ไม่เท่ากันได้จริงมี 3 ทาง — เขียนให้ครบ ไม่งั้น HR เจอแล้วแจ้งว่าเป็นบั๊ก
 */
const OT_DIFF_REASONS =
  'สองคอลัมน์ปกติเท่ากัน ที่ต่างกันได้คือ (1) มาสแกนทำงานวันหยุด ระบบคิดค่าทำงานวันหยุดให้เองโดยไม่ต้องมีใบ OT ' +
  'ฝั่งระบบจึงมากกว่า (2) ยังไม่ได้รัน "สรุปเวลาเข้างาน" ใหม่หลังอนุมัติใบเพิ่ม ' +
  '(3) เศษนาทีปัดคนละขั้น (ไฟล์นี้ปัดทีละวันเพื่อให้รายวันบวกกันได้ยอดสรุป ส่วนฝั่งระบบปัดครั้งเดียวตอนท้าย) ' +
  'ต่างกันได้ไม่เกินเศษสตางค์ของชั่วโมง';

/**
 * ⚠️ แยกคนละเรื่องกับส่วนต่างข้างบน — สองคอลัมน์ "ชั่วโมง" เท่ากัน แต่ไม่ได้เงิน
 * (payroll.ts: โหมดเงินเดือนล้วนและรายชั่วโมงข้ามการคิด OT ทั้งก้อน)
 * ถ้าไม่บอก HR จะเห็นชั่วโมงในไฟล์แล้วคาดว่าต้องมีเงิน OT ในสลิป
 */
const OT_NO_PAY_NOTE =
  'หมายเหตุ: พนักงานโหมดเงินเดือนล้วนและพนักงานรายชั่วโมง จะมีชั่วโมง OT ขึ้นในไฟล์นี้ แต่ระบบไม่จ่ายเงิน OT ให้';

/**
 * ⭐ ดึงเป็นช่วงวันที่เอง (รายวัน/รายสัปดาห์) ไฟล์จะไม่มีคอลัมน์ฝั่งคิดเงิน — ห้ามอ้างถึง
 * คอลัมน์ที่ไม่มีอยู่ในไฟล์ ไม่งั้น HR ไล่หาไม่เจอแล้วนึกว่าไฟล์เสีย
 */
function otNote(withPayroll: boolean): string {
  if (!withPayroll) {
    return (
      'คอลัมน์ "OT อนุมัติ" = ชั่วโมงรวมจากใบ OT ที่อนุมัติในช่วงนี้ — ' +
      'ถ้าอยากวางเทียบกับชั่วโมงที่ระบบสรุปไว้ตอนคิดเงิน ให้ดึงเป็นงวดรายเดือน จะมีคอลัมน์ "OT (ระบบสรุป)" เพิ่มมา'
    );
  }
  return (
    'คอลัมน์ "OT อนุมัติ" = ชั่วโมงรวมจากใบ OT ที่อนุมัติ · คอลัมน์ "OT (ระบบสรุป)" = ชั่วโมงที่ระบบสรุปไว้ตอนรัน "สรุปเวลาเข้างาน" — ' +
    OT_DIFF_REASONS
  );
}

/** เวลาไทยอ่านง่าย: "17 ก.ค. 2569 17:48" */
const thaiDateTime = (iso: string) =>
  new Date(iso).toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

/**
 * บรรทัดหัวไฟล์ที่บอก "ตัวเลขฝั่งระบบคิดเงินเป็นภาพเมื่อไหร่" — สำคัญกว่าที่คิด:
 * ตัวเลขนั้นถูกปั๊มไว้ตอนรันสรุปเวลา ไม่ได้คำนวณสดตอนดึงไฟล์ ถ้าไม่บอก HR จะเทียบ
 * สองคอลัมน์แล้วสรุปผิดว่าระบบคำนวณพลาด
 */
function headerNote(data: AttendanceExport): string {
  // ช่วงที่กำหนดเอง (เช่นรอบ 26 ชน 25) ไม่มีคอลัมน์ฝั่งคิดเงินให้เทียบ — บอกว่าทำไม
  // ไม่งั้น HR หาคอลัมน์ที่เคยเห็นไม่เจอ แล้วนึกว่าไฟล์เสีย
  if (!data.payroll_comparable) {
    return 'ไฟล์นี้ดึงตามช่วงวันที่ที่กำหนดเอง จึงมีเฉพาะตัวเลขที่นับจากสแกนจริงในช่วงนั้น — คอลัมน์ "ระบบคิดเงิน" ตัดออก เพราะระบบคิดเงินสรุปเป็นงวดรายเดือน (วันที่ 1 ถึงสิ้นเดือน) คนละช่วงวันที่กับไฟล์นี้ เอามาวางเทียบกันตรง ๆ ไม่ได้';
  }
  if (!data.reconciled) {
    return 'งวดนี้ยังไม่ได้รัน "สรุปเวลาเข้างาน" — คอลัมน์ฝั่งระบบคิดเงินจึงยังว่าง (คอลัมน์ที่นับจากสแกนคือภาพปัจจุบัน ใช้ได้เลย)';
  }
  const at = data.summary_computed_at ? thaiDateTime(data.summary_computed_at) : 'ไม่ทราบเวลา';
  if (data.summary_stale) {
    return `คอลัมน์ "ระบบคิดเงิน" = ภาพ ณ ตอนสรุปเวลาล่าสุด ${at} และยังไม่ตามข้อมูลล่าสุด (${data.summary_stale_reason ?? 'มีข้อมูลเปลี่ยนหลังจากนั้น'}) — คอลัมน์ที่นับจากสแกนคือภาพปัจจุบัน ให้รัน "สรุปเวลาเข้างาน" ใหม่ก่อนปิดงวด`;
  }
  return `คอลัมน์ "ระบบคิดเงิน" = ภาพ ณ ตอนสรุปเวลาล่าสุด ${at} (ตรงกับข้อมูลปัจจุบันแล้ว)`;
}

/**
 * @param periodLabel ข้อความงวดที่โชว์หัวไฟล์ เช่น "กรกฎาคม 2569"
 * @param onlyEmp     ถ้าไม่ null = เอาเฉพาะคนนั้น
 * @returns CSV พร้อม BOM นำหน้า (กันภาษาไทยเพี้ยนตอนเปิดใน Excel)
 */
export function buildAttendanceCsv(
  data: AttendanceExport,
  periodLabel: string,
  onlyEmp: string | null = null,
): string {
  const keep = <T extends { emp_code: string }>(arr: T[]) =>
    onlyEmp ? arr.filter((x) => x.emp_code === onlyEmp) : arr;

  // ── ส่วนบน: สรุปรายคน (นับสด | ที่ระบบใช้คิดเงิน)
  // คอลัมน์ฝั่ง "ระบบคิดเงิน" มีเฉพาะตอนดึงเป็นงวดปฏิทิน — ช่วงที่กำหนดเองไม่มีงวด
  // ให้เทียบ ถ้าปล่อยคอลัมน์ว่างไว้ HR จะอ่านว่า "ระบบยังไม่ได้สรุปเวลา" ซึ่งไม่จริง
  const withPayroll = data.payroll_comparable;
  const sumHeader = [
    // fb#492 — TN เน้นตำแหน่งเป็นหลักในทุกรายงาน → ตำแหน่ง แล้วแผนก (fb#484 เคยเอาแผนกขึ้นก่อน)
    'รหัส', 'ชื่อ', 'ตำแหน่ง', 'แผนก',
    'วันทำงาน(นับจากสแกน)', 'สแกนไม่ครบ(วัน)', 'ลา(วัน)', 'ขาด(วัน)', 'สาย(วัน)', 'สาย(นาที)', 'วันหยุด(วัน)',
    'OT อนุมัติ(ชม.)', 'OT อนุมัติ(ใบ)',
    ...(withPayroll
      ? ['วันทำงาน(ระบบคิดเงิน)', 'ขาด(ระบบคิดเงิน)', 'ลาได้เงิน(ระบบ)', 'ลาไม่ได้เงิน(ระบบ)', 'สาย-วัน(ระบบ)', 'สาย-นาที(ระบบ)', 'OT (ระบบสรุป)(ชม.)']
      : []),
  ];
  const sumRows = keep(data.by_emp).map((e) => [
    e.emp_code, e.emp_name, e.position, e.department,
    round(e.counted.working_days), round(e.counted.incomplete_scan_days), round(e.counted.leave_days),
    round(e.counted.absent_days), round(e.counted.late_days), round(e.counted.late_minutes), round(e.counted.holiday_days),
    round(e.counted.ot_hours), e.counted.ot_count,
    ...(withPayroll
      ? [
          e.payroll ? round(e.payroll.working_days) : 'ยังไม่ได้สรุปเวลา',
          e.payroll ? round(e.payroll.absent_days) : '',
          e.payroll ? round(e.payroll.paid_leave_days) : '',
          e.payroll ? round(e.payroll.unpaid_leave_days) : '',
          e.payroll ? round(e.payroll.late_days) : '',
          e.payroll ? round(e.payroll.late_minutes) : '',
          e.payroll ? round(e.payroll.ot_hours) : '',
        ]
      : []),
  ]);

  // ── ส่วนล่าง: รายวัน (เรียงตามคน แล้วตามวันที่ — อ่านไล่ทีละคนได้)
  const detailHeader = [
    'วันที่', 'รหัส', 'ชื่อ', 'ตำแหน่ง', 'แผนก', 'กะ', 'เข้ากะ', 'เลิกกะ',
    'สแกนเข้า', 'สแกนออก', 'สาย(นาที)', 'สถานะ', 'ประเภทลา', 'OT(ชม.)', 'ชนิด OT', 'หมายเหตุ',
  ];
  const sorted = [...keep(data.rows)].sort(
    (a, b) => a.emp_code.localeCompare(b.emp_code) || a.date.localeCompare(b.date),
  );
  const detailRows = sorted.map((r) => [
    r.date, r.emp_code, r.emp_name, r.position, r.department, r.shift_code, r.shift_start, r.shift_end,
    r.in_at, r.out_at, r.late_minutes, r.status_label, r.leave_type,
    r.ot_hours || '', r.ot_type ? (OT_TYPE_LABEL[r.ot_type] ?? r.ot_type) : '',
    r.note,
  ]);

  const line = (cells: (string | number)[]) => cells.map(esc).join(',');
  const out = [
    line([`รายงานเวลาเข้า-ออก ลา และ OT (${periodLabel})`]),
    line([headerNote(data)]),
    line([otNote(withPayroll)]),
    line([OT_NO_PAY_NOTE]),
    line(sumHeader),
    ...sumRows.map(line),
    '', // บรรทัดว่างคั่น 2 ส่วน
    line(['รายละเอียดรายวัน']),
    line(detailHeader),
    ...detailRows.map(line),
  ];
  return '﻿' + out.join('\n') + '\n';
}
